import { AnyVariables, CombinedError, UseQueryState } from "urql";

import { assertNever } from "./assertNever";

export interface UseQueryIdleState<Data, Variables extends AnyVariables = AnyVariables>
    extends UseQueryState<Data, Variables> {
    fetching: false;
    stale: false;
    data: undefined;
    error: undefined;
}
export interface UseQueryLoadingState<Data, Variables extends AnyVariables = AnyVariables>
    extends UseQueryState<Data, Variables> {
    fetching: true;
    data: undefined;
}
export interface UseQueryLoadedState<Data, Variables extends AnyVariables = AnyVariables>
    extends UseQueryState<Data, Variables> {
    fetching: false;
    data: Data;
}
export interface UseQueryReloadingState<Data, Variables extends AnyVariables = AnyVariables>
    extends UseQueryState<Data, Variables> {
    fetching: true;
    data: Data;
}
export interface UseQueryErrorState<Data, Variables extends AnyVariables = AnyVariables>
    extends UseQueryState<Data, Variables> {
    error: CombinedError;
}

export type IQueryStateVisitor<Data, Return> =
    | IQueryStateVisitor.Verbose<Data, Return>
    | IQueryStateVisitor.Compact<Data, Return>;
export namespace IQueryStateVisitor {
    export interface Verbose<Data, Return> {
        idle(state: UseQueryIdleState<Data>): Return;
        loading(state: UseQueryLoadingState<Data>): Return;
        success(state: UseQueryLoadedState<Data>): Return;
        reloading(state: UseQueryReloadingState<Data>): Return;
        error(state: UseQueryErrorState<Data>): Return;
    }

    export interface Compact<Data, Return> {
        notLoaded(state: UseQueryIdleState<Data> | UseQueryLoadingState<Data>): Return;
        loaded(state: UseQueryLoadedState<Data> | UseQueryReloadingState<Data>): Return;
        reloading?(state: UseQueryReloadingState<Data>): Return;
        error(state: UseQueryErrorState<Data>): Return;
    }
}

export function visitQueryState<Data, Return>(
    queryState: UseQueryState<NonNullable<Data>, AnyVariables>,
    visitor: IQueryStateVisitor<NonNullable<Data>, Return>,
): Return {
    const { fetching, data, error } = queryState;

    if (isVerbose(visitor)) {
        if (data != null) {
            if (fetching) {
                return visitor.reloading(queryState as never);
            }
            return visitor.success(queryState as never);
        }
        if (error != null) {
            return visitor.error(queryState as never);
        }
        if (fetching) {
            return visitor.loading(queryState as never);
        }
        return visitor.idle(queryState as never);
    }

    if (isCompact(visitor)) {
        if (data != null) {
            if (fetching && visitor.reloading != null) {
                return visitor.reloading(queryState as never);
            }
            return visitor.loaded(queryState as never);
        }
        if (error != null) {
            return visitor.error(queryState as never);
        }
        return visitor.notLoaded(queryState as never);
    }

    return assertNever(visitor);
}

function isVerbose<Data, Return>(
    visitor: IQueryStateVisitor<Data, Return>,
): visitor is IQueryStateVisitor.Verbose<Data, Return> {
    return (visitor as IQueryStateVisitor.Verbose<Data, Return>).idle != null;
}

function isCompact<Data, Return>(
    visitor: IQueryStateVisitor<Data, Return>,
): visitor is IQueryStateVisitor.Compact<Data, Return> {
    return (visitor as IQueryStateVisitor.Compact<Data, Return>).notLoaded != null;
}
